import { defineStore } from "pinia";
import { setLocalStorage, getLocalStorage, DecimalUtils } from "@/utils/common";
import { kvCacheSet, kvCacheGet } from "@/utils/kvCache";
import { useConfigStore } from "@/store/config";

// ============================================================================
//  离线挖矿本地存储 Key 常量
// ============================================================================
const OFFLINE_MINING_START_KEY = "offline_mining_start"; // 离线挖矿开始时间戳
const OFFLINE_MINING_YIELD_KEY = "offline_await_claim"; // 离线累计产出 (nano)

export const useUserStore = defineStore("userStore", {
  state: () => ({
    // ====================================================================
    //  用户基础信息
    // ====================================================================
    uid: "",
    balance: 0,
    points: 0,
    referrals: "",
    nickname: "",
    username: "",
    mobile: "",
    email: "",
    password: "",
    twa_premium: 0,
    ton_address: "",
    okx_ton_address: "",
    withdraw_address: "",
    withdraw_record: [],
    web_withdraw_record: [],
    commission: 0,
    token_balances: {},
    star_pay: "",
    payment_type: "",
    payment_info: "",
    share_group_num: kvCacheGet("share_group_num") || 0,
    extraData: {},

    // ====================================================================
    //  登录状态
    // ====================================================================
    isLogin: false,

    // ====================================================================
    //  挖矿状态 (已登录)
    // ====================================================================
    task01: 0, // 挖矿开始时间戳
    task02: 0,
    nowtime: 0, // 服务端时间
    profit: 0, // 每秒产出 = 免费算力 + 租用算力
    hashrate: 0, // 算力哈希
    await_claim: "", // 待领取 (nano)

    // ====================================================================
    //  离线挖矿状态 (未登录) - 使用 coin_yield 免费产出
    // ====================================================================
    offlineMiningStart: parseInt(
      getLocalStorage(OFFLINE_MINING_START_KEY) || "0",
    ),
    offlineAwaitClaim: getLocalStorage(OFFLINE_MINING_YIELD_KEY) || "0",
    task_complete_nums: Number(kvCacheGet("task_complete_nums") || 0),
    my_miners: [], // 我的矿机列表
  }),
  actions: {
    // ====================================================================
    //  登录状态管理
    // ====================================================================
    setIsLogin(status) {
      this.isLogin = status;
    },

    // ====================================================================
    //  离线挖矿 (未登录时使用)
    // ====================================================================

    /**
     * 开始离线挖矿 - 记录开始时间戳到本地存储
     */
    startOfflineMining(timestamp) {
      this.offlineMiningStart = timestamp;
      setLocalStorage(OFFLINE_MINING_START_KEY, String(timestamp));
    },

    /**
     * 更新离线累计产出
     */
    setOfflineAwaitClaim(value) {
      this.offlineAwaitClaim = value;
      setLocalStorage(OFFLINE_MINING_YIELD_KEY, value);
    },

    /**
     * 登录成功后合并离线挖矿数据
     * @returns {string} 离线累计产出 (nano)
     */
    mergeOfflineMiningData() {
      const offlineYield = this.offlineAwaitClaim || "0";
      // 清除离线挖矿数据
      this.clearOfflineMining();
      return offlineYield;
    },

    /**
     * 清除离线挖矿数据
     */
    clearOfflineMining() {
      this.offlineMiningStart = 0;
      this.offlineAwaitClaim = "0";
      setLocalStorage(OFFLINE_MINING_START_KEY, "0");
      setLocalStorage(OFFLINE_MINING_YIELD_KEY, "0");
    },

    // ====================================================================
    //  已登录用户数据初始化
    // ====================================================================
    initAmount(balance, points) {
      this.balance = balance;
      this.points = points;
    },
    initMiner(profit, hashrate) {
      this.profit = profit;
      this.hashrate = hashrate;
    },
    initAwaitClaim(await_claim) {
      this.await_claim = await_claim;
    },
    initWithdrawAddress(withdraw_address) {
      this.withdraw_address = withdraw_address;
    },
    initWithdrawRecord(withdraw_record) {
      this.withdraw_record = withdraw_record;
    },
    initWithdrawWebRecord(withdraw_record) {
      this.web_withdraw_record = withdraw_record;
    },
    setPaymentInfo(payment_info, payment_type) {
      this.payment_info = payment_info;
      this.payment_type = payment_type;
    },
    addNowtime() {
      this.nowtime++;
    },
    addShareGroupNum() {
      this.share_group_num++;
    },
    setBalanceRaw(balance) {
      this.balance = Number(balance);
    },
    subtractBalance(balance) {
      this.balance = DecimalUtils.subtract(this.balance, balance);
    },
    subtractTokenBalance(value) {
      const config = useConfigStore();
      this.token_balances[config.auto_pay_coin] = DecimalUtils.subtract(
        this.token_balances[config.auto_pay_coin],
        value,
      );
    },
    addTokenBalance(coin, value) {
      console.log("coin", coin);
      console.log("value", value);
      this.token_balances[coin] = DecimalUtils.add(
        this.token_balances[coin],
        value,
      );
    },
    setBalance(update_count) {
      if (update_count == 0) return;
      if (this.balance + update_count < 0) return;

      const cacheKey = "google_b";
      let localValue = parseInt(getLocalStorage(cacheKey) || "0", 10);
      let newValue = localValue + update_count;
      setLocalStorage(cacheKey, newValue.toString());
      this.balance = this.balance + update_count;
    },
    setPoints(update_count) {
      if (update_count == 0) return;
      if (this.points + update_count < 0) return;

      const cacheKey = "google_p";
      let localValue = parseInt(getLocalStorage(cacheKey) || "0", 10);
      let newValue = localValue + update_count;
      setLocalStorage(cacheKey, newValue.toString());
      this.points = this.points + update_count;
    },
    setTonAddress(address) {
      this.ton_address = address;
    },
    setTask01(data) {
      this.task01 = data;
    },
    setDATA(data) {
      for (const key in data) {
        if (key in this.$state && key !== "extraData") {
          this[key] = data[key];
        } else {
          this.extraData[key] = data[key];
        }
      }
    },
    getUserData(key) {
      if (key in this.$state && key !== "extraData") {
        return this[key];
      } else if (key in this.extraData) {
        return this.extraData[key];
      }
      return undefined;
    },
    setOKXTonAddress(address) {
      this.okx_ton_address = address;
    },
    addTaskCompleteNums(nums) {
      this.task_complete_nums += Number(nums);
      kvCacheSet("task_complete_nums", this.task_complete_nums);
    },
    setMyMiners(my_miners) {
      this.my_miners = my_miners;
    },
  },
});
